// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
  Link,
  Box,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Cancel as RejectedIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as PendentIcon,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  InfoOutlined as InfoIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const maxSize = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);
const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  documentItem: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  uploadArea: {
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    minHeight: 140,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[100],
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
  },
  sectionSeparator: {
    height: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.grey[200],
    margin: theme.spacing(2, 0),
  },
  fileName: {
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    maxWidth: '100%',
    lineHeight: 1.2,
  },
  thumbnail: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  pdfThumbnail: {
    width: 60,
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  exampleThumbnail: {
    width: 120,
    height: 80,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1.5),
    border: `3px solid ${theme.palette.primary.light}`,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      borderColor: theme.palette.primary.main,
    },
  },
  exampleImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.3s ease',
  },
  exampleOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.3) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    '&:hover': {
      opacity: 1,
    },
  },
  exampleLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%)',
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '8px 6px 6px',
    textAlign: 'center',
    letterSpacing: '0.5px',
  },
  helpContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
  },
  helpButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(25,118,210,0.4)',
      textDecoration: 'none',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  documentTypeTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  uploadInstruction: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  exampleBadge: {
    position: 'absolute',
    top: -8,
    left: -8,
    backgroundColor: theme.palette.success.main,
    color: 'white',
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  uploadAreaContent: {
    position: 'relative',
    zIndex: 0,
  },
  uploadDropArea: {
    border: `2px dashed ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + '10',
    },
  },
  exampleClickable: {
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      opacity: 0.8,
    },
  },
  uploadAreaSeparator: {
    margin: theme.spacing(1.5, 0),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  uploadedThumbnail: {
    width: '100%',
    height: 100,
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.success.main}`,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadedImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },
  uploadedPdfContainer: {
    width: '100%',
    height: 100,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.success.main}`,
    position: 'relative',
  },
  uploadedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    '&:hover': {
      opacity: 1,
    },
  },
  uploadedActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(1),
  },
  uploadButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(25,118,210,0.4)',
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[600],
    },
  },
  deleteButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light + '20',
    },
  },
  uploadedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  thumbnailContainer: {
    position: 'relative',
    marginBottom: theme.spacing(1),
  },
  successBadge: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.palette.success.main,
    color: 'white',
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 1,
  },
}));

const ProcessDocumentData = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { code, process, expanded, disabled, onExpanded, onSave, onDelete } = props;

  const [files, setFiles] = useState({});
  const [thumbnails, setThumbnails] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  // Helper function to check if LEGAL_ID is uploaded or selected
  const isLegalIdAvailable = () => {
    // Check if LEGAL_ID file is selected locally
    if (files['LEGAL_ID']) {
      return true;
    }
    // Check if LEGAL_ID is already uploaded in the process
    return process.files.some(file => file.fileType === 'LEGAL_ID');
  };

  // Function to filter pending document types based on dependencies
  const getFilteredPendingDocuments = () => {
    const pendingDocs = process.pendentDocumentTypes();
    
    // If LEGAL_ID_BACK is in pending documents but LEGAL_ID is not available, filter it out
    return pendingDocs.filter(docType => {
      if (docType === 'LEGAL_ID_BACK') {
        return isLegalIdAvailable();
      }
      return true;
    });
  };

  // Function to generate thumbnail preview
  const generateThumbnail = (file, fileType) => {
    if (!file) return null;

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnails(prev => ({
          ...prev,
          [fileType]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      setThumbnails(prev => ({
        ...prev,
        [fileType]: 'pdf'
      }));
    }
  };

  // Function to render help link for RUC
  const renderHelpLink = (fileType) => {
    if (fileType === 'RUC') {
      return (
        <div className={classes.helpContainer}>
          <Tooltip title={t('Get help with RUC document requirements')} arrow>
            <Link
              href="#"
              className={classes.helpButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // TODO: Define help action
                console.log('Help for RUC document');
              }}
            >
              <InfoIcon style={{ fontSize: 16 }} />
              {t('Help')}
            </Link>
          </Tooltip>
        </div>
      );
    }
    return null;
  };

  // Function to handle example image click
  const handleExampleClick = (fileType) => {
    console.log({fileType});

    // const getImageAndText = (type) => {
    //   switch (type) {
    //     case 'LEGAL_ID':
    //       return { 
    //         image:  `${baseUrl}/images/cedulafront.png`,
    //         link:  `#`,
    //         text: t('ID Front'),
    //         description: t('Photo of front side of your ID card')
    //       };
    //     case 'LEGAL_ID_BACK':
    //       return { 
    //         image:  `${baseUrl}/images/cedulaback.png`,
    //         link:  `#`,
    //         text: t('ID Back'),
    //         description: t('Photo of back side of your ID card')
    //       };
    //     case 'RUC':
    //       return { 
    //         image:  `${baseUrl}/images/ruc.png`,
    //         link: 'https://ecuafirma.com/todas-las-firmas-electronicas-funcionan-para-facturacion-electronica/',
    //         text: t('RUC Document'),
    //         description: t('Photo of tax registration certificate')
    //       };
    //     case 'BANK_CERTIFICATION':
    //       return { 
    //         image:  `${baseUrl}/images/bank_certification.png`,
    //         link:  `#`,
    //         text: t('Bank Certificate'),
    //         description: t('Photo of bank account certification')
    //       };
    //     case 'BASIC_SERVICES':
    //       return { 
    //         image:  `${baseUrl}/images/basic_services.png`,
    //         link:  `#`,
    //         text: t('Service Bill'),
    //         description: t('Photo of utility bill or service receipt')
    //       };
    //     default:
    //       return { 
    //         image:  `${baseUrl}/images/document.png`,
    //         link:  `#`,
    //         text: t('Document'),
    //         description: t('Photo of required document')
    //       };
    //   }
    // };

    // const { link } = getImageAndText(fileType);
    // window.open(link, '_blank');
  };

  // Function to render example thumbnail for each document type
  const renderExampleThumbnail = (fileType) => {
    const getImageAndText = (type) => {
      switch (type) {
        case 'LEGAL_ID':
          return { 
            image:  `${baseUrl}/images/cedulafront.png`,
            text: t('ID Front'),
            description: t('Photo of front side of your ID card')
          };
        case 'LEGAL_ID_BACK':
          return { 
            image:  `${baseUrl}/images/cedulaback.png`,
            text: t('ID Back'),
            description: t('Photo of back side of your ID card')
          };
        case 'RUC':
          return { 
            image:  `${baseUrl}/images/ruc.png`,
            text: t('RUC Document'),
            description: t('Photo of tax registration certificate')
          };
        case 'BANK_CERTIFICATION':
          return { 
            image:  `${baseUrl}/images/bank_certification.png`,
            text: t('Bank Certificate'),
            description: t('Photo of bank account certification')
          };
        case 'BASIC_SERVICES':
          return { 
            image:  `${baseUrl}/images/basic_services.png`,
            text: t('Service Bill'),
            description: t('Photo of utility bill or service receipt')
          };
        default:
          return { 
            image:  `${baseUrl}/images/document.png`,
            text: t('Document'),
            description: t('Photo of required document')
          };
      }
    };

    const { image, text, description } = getImageAndText(fileType);
    
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Tooltip title={t('Click to see example in detail')} arrow>
          <div 
            className={`${classes.exampleThumbnail} ${classes.exampleClickable}`}
            onClick={() => handleExampleClick(fileType)}
          >
            <div className={classes.exampleBadge}>x</div>
            <img 
              src={image} 
              alt={`Example ${fileType}`} 
              className={classes.exampleImage}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Eb2N1bWVudCBFeGFtcGxlPC90ZXh0Pgo8L3N2Zz4K';
              }}
            />
            <div className={classes.exampleOverlay}>
              <Typography variant="caption" style={{ color: 'white', fontWeight: 'bold' }}>
                {t('View Details')}
              </Typography>
            </div>
            <div className={classes.exampleLabel}>
              {text}
            </div>
          </div>
        </Tooltip>
        <Typography variant="caption" color="textSecondary" align="center" style={{ maxWidth: 120, lineHeight: 1.3 }}>
          {description}
        </Typography>
      </Box>
    );
  };

  // Function to render upload area
  const renderUploadArea = (fileType, id) => {
    return (
      <div className={classes.uploadDropArea}>
        <input
          accept={fileType !== 'LEGAL_ID' && fileType !== 'LEGAL_ID_BACK' ? '.png,.jpg,.pdf' : '.png,.jpg'}
          className={classes.upload}
          id={id}
          type="file"
          onChange={(e) => {
            if (e.target.files && e.target.files.length) {
              const file = e.target.files[0];
              if (file.size >= maxSize) {
                enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
                  variant: 'error',
                });
              } else {
                let docs = { ...files };
                docs[fileType] = file;
                setFiles(docs);
                generateThumbnail(file, fileType);
              }
            }
          }}
        />
        <label htmlFor={id} style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
          <AddIcon color="action" style={{ fontSize: 32, marginBottom: 8 }} />
          <Typography variant="body2" gutterBottom>
            {t('Take or select photo to upload')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fileType !== 'LEGAL_ID' && fileType !== 'LEGAL_ID_BACK' 
              ? t('Photo in JPG, PNG or PDF format')
              : t('Photo in JPG or PNG format')
            }
          </Typography>
        </label>
      </div>
    );
  };

  // Function to render uploaded file thumbnail
  const renderUploadedThumbnail = (fileType) => {
    const thumbnail = thumbnails[fileType];
    const file = files[fileType];
    const isLoading = isDocumentLoading(fileType);
    
    if (!file) return null;

    return (
      <div className={classes.uploadedContainer}>
        <div className={classes.thumbnailContainer}>
          {thumbnail === 'pdf' ? (
            <div className={classes.uploadedPdfContainer}>
              <PdfIcon style={{ fontSize: 32, color: '#d32f2f' }} />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                PDF
              </Typography>
            </div>
          ) : thumbnail ? (
            <div className={classes.uploadedThumbnail}>
              <img 
                src={thumbnail} 
                alt="Uploaded file" 
                className={classes.uploadedImage}
              />
            </div>
          ) : (
            <div className={classes.uploadedPdfContainer}>
              <DocumentIcon style={{ fontSize: 32 }} />
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                {t('File')}
              </Typography>
            </div>
          )}
          <div className={classes.successBadge}>✓</div>
        </div>
        
        <Typography className={classes.uploadedFileName} title={file.name}>
          {file.name}
        </Typography>
        
        <div className={classes.uploadedActions}>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            onClick={(e) => {
              e.preventDefault();
              handleDocumentSave(fileType, file);
            }}
            disabled={disabled || isLoading}
            className={classes.uploadButton}
            size="small"
          >
            {isLoading ? t('Uploading...') : t('Upload Document')}
          </Button>
          
          <Tooltip title={t('Remove file')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                let docs = { ...files };
                delete docs[fileType];
                setFiles(docs);
                setThumbnails(prev => {
                  const newThumbnails = { ...prev };
                  delete newThumbnails[fileType];
                  return newThumbnails;
                });
              }}
              disabled={disabled || isLoading}
              className={classes.deleteButton}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  // Function to set loading state for a specific document type
  const setDocumentLoading = (fileType, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [fileType]: isLoading
    }));
  };

  // Function to check if a specific document is loading
  const isDocumentLoading = (fileType) => {
    return loadingStates[fileType] || false;
  };

  // Modified onSave function to handle loading per document
  const handleDocumentSave = async (fileType, file) => {
    setDocumentLoading(fileType, true);
    try {
      await onSave({
        fileType: fileType,
        file: file,
      });
    } finally {
      setDocumentLoading(fileType, false);
    }
  };

  let processStatusText = process.hasDocuments() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasDocuments() ? <DoneIcon /> : <EditIcon />;
  let statusColor = process.hasDocuments() ? 'primary' : 'default';
  
  if (process.hasDocumentsValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon />;
    statusColor = 'success';
  }

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
        className={classes.accordionSummary}
      >
        <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DocumentIcon /> {t('Documents data')}
        </Typography>
        <Chip
          icon={processStatusIcon}
          label={t(processStatusText)}
          color={statusColor}
          size="small"
          className={classes.statusChip}
        />
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Existing Documents */}
          {process.files.length > 0 && (
            <Grid item xs={12}>
              <Paper className={classes.sectionPaper} elevation={0}>
                <Typography variant="subtitle2" gutterBottom color="textSecondary">
                  {t('Uploaded Documents')}
                </Typography>
                <Grid container spacing={2}>
                  {process.files.map((item) => (
                    <Grid item xs={12} sm={6} key={`${item.fileType}-${item.name}`}>
                      <Paper className={classes.documentItem}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item>
                            {item.approved ? (
                              <DoneIcon color="primary" />
                            ) : item.rejected ? (
                              <RejectedIcon color="secondary" />
                            ) : (
                              <PendentIcon color="action" />
                            )}
                          </Grid>
                          <Grid item xs>
                            <Typography variant="body2" color="textSecondary">
                              {t(`Document ${item.fileType}`)}
                            </Typography>
                            <Typography variant="subtitle2" className={classes.fileName} title={item.name}>
                              <strong>{item.name}</strong>
                            </Typography>
                            {item.message && (
                              <Chip 
                                label={item.message} 
                                color={item.approved ? 'primary' : 'secondary'} 
                                size="small"
                                style={{ marginTop: 4 }}
                              />
                            )}
                          </Grid>
                          <Grid item>
                            {(!disabled || item.rejected) && (
                              <IconButton
                                aria-label="delete"
                                onClick={() => {
                                  onDelete({
                                    fileType: item.fileType,
                                  });
                                }}
                                className={classes.error}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Document Upload Section */}
          {getFilteredPendingDocuments().length > 0 && (
            <Grid item xs={12}>
              <Paper className={classes.sectionPaper} elevation={0}>
                <Typography variant="subtitle2" gutterBottom color="textSecondary">
                  {t('Required Documents')}
                </Typography>
                <Grid container spacing={2}>
                  {getFilteredPendingDocuments().map((item, index) => {
                    const id = `upload-document-${item}-${index}`;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={id}>
                        <Paper className={classes.uploadArea} elevation={2}>
                          {renderHelpLink(item)}
                          <div className={classes.uploadAreaContent}>
                            {/* Siempre mostrar el ejemplo de guía */}
                            {renderExampleThumbnail(item)}
                            <Typography className={classes.documentTypeTitle} gutterBottom>
                              {t(`Add document ${item}`)}
                            </Typography>
                            
                            {/* Mostrar área de upload o archivo subido */}
                            {!files[item] ? (
                              renderUploadArea(item, id)
                            ) : (
                              renderUploadedThumbnail(item)
                            )}
                          </div>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessDocumentData;
